import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../auth/AuthContext";
import ParkingCard from "../components/ParkingCard";
import ParkingMap from "../components/ParkingMap";
import { Search, Filter as FilterIcon, RotateCcw, List, Map as MapIcon } from "lucide-react";
import { pageItems } from "../lib/pageItems";
import { PARKING_SPACE_TYPES } from "../lib/parkingListingConstants";

const FACILITY_OPTIONS = [
  ["camera", "Camera security"],
  ["security_24_7", "24/7 security"],
  ["ev_charging", "EV charging"],
  ["covered_roof", "Covered roof"],
  ["gated", "Gated property"],
  ["fenced", "Fenced location"],
];

export default function FindParking() {
  const [params, setParams] = useSearchParams();
  const [spots, setSpots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [filters, setFilters] = useState({
    q: params.get("q") || "",
    city: params.get("city") || "",
    space_type: params.get("space_type") || "",
    facilities: (params.get("facilities") || "").split(",").filter(Boolean),
    min_price: params.get("min_price") || "",
    max_price: params.get("max_price") || "",
    sort: params.get("sort") || "price_asc",
  });

  const fetchSpots = useCallback(async (f) => {
    setLoading(true);
    try {
      const cleaned = {};
      Object.entries(f).forEach(([k, v]) => {
        if (k === "facilities") { if (v.length) cleaned.facilities = v.join(","); }
        else if (v !== "" && v != null) cleaned[k] = v;
      });
      const r = await axios.get(`${API}/parking`, { params: cleaned });
      setSpots(pageItems(r.data));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSpots(filters); }, []); // eslint-disable-line

  const apply = () => {
    const cleaned = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (k === "facilities") { if (v.length) cleaned.facilities = v.join(","); }
      else if (v !== "") cleaned[k] = v;
    });
    setParams(cleaned);
    fetchSpots(filters);
  };
  const reset = () => {
    const empty = { q: "", city: "", space_type: "", facilities: [], min_price: "", max_price: "", sort: "price_asc" };
    setFilters(empty); setParams({}); fetchSpots(empty);
  };

  const toggleFac = (val) => setFilters(f => ({
    ...f, facilities: f.facilities.includes(val) ? f.facilities.filter(x => x !== val) : [...f.facilities, val]
  }));

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="find-parking-page">
      <div className="mb-10">
        <div className="eyebrow mb-2">Marketplace</div>
        <h1 className="zc-headline text-4xl md:text-5xl">Find a parking space</h1>
        <p className="text-[var(--zc-text-2)] mt-3 max-w-xl">Hourly or daily, near where you need it. Driveways, garages, commercial lots — verified hosts.</p>
      </div>

      <div className="grid lg:grid-cols-[280px,1fr] gap-8">
        <aside className="zc-card p-6 h-fit lg:sticky lg:top-24" data-testid="parking-filters">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2 font-semibold text-sm"><FilterIcon size={14} /> Filters</div>
            <button onClick={reset} className="text-xs text-[var(--zc-text-3)] hover:text-[var(--zc-accent)] flex items-center gap-1" data-testid="pfilter-reset">
              <RotateCcw size={11} /> Reset
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-[var(--zc-text-3)]">Where</label>
              <input className="zc-input h-10 px-3 w-full text-sm mt-1" value={filters.city}
                onChange={e => setFilters(f => ({ ...f, city: e.target.value }))} data-testid="pfilter-city"
                placeholder="City (e.g. New York)" />
            </div>
            <div>
              <label className="text-xs text-[var(--zc-text-3)] block mb-2">Space type</label>
              <div className="flex flex-wrap gap-1.5">
                <button
                  key="all-types"
                  type="button"
                  onClick={() => setFilters(f => ({ ...f, space_type: "" }))}
                  className={"px-3 h-8 text-xs zc-chip " + (!filters.space_type ? "zc-chip-active" : "")}
                  data-testid="pfilter-type-all"
                >
                  All types
                </button>
                {PARKING_SPACE_TYPES.map(([val, lbl]) => (
                  <button key={val} type="button"
                    onClick={() => setFilters(f => ({ ...f, space_type: f.space_type === val ? "" : val }))}
                    className={"px-3 h-8 text-xs zc-chip " + (filters.space_type === val ? "zc-chip-active" : "")}
                    data-testid={`pfilter-type-${val}`}>{lbl}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-xs text-[var(--zc-text-3)] block mb-2">Facilities</label>
              <div className="space-y-2">
                {FACILITY_OPTIONS.map(([val, lbl]) => (
                  <label key={val} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`pfilter-fac-${val}-label`}>
                    <input type="checkbox" checked={filters.facilities.includes(val)} onChange={() => toggleFac(val)}
                      className="w-4 h-4 accent-[var(--zc-accent)]" data-testid={`pfilter-fac-${val}`} />
                    <span className="text-[var(--zc-text)]">{lbl}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[var(--zc-text-3)]">Min $/hr</label>
                <input className="zc-input h-10 px-3 w-full text-sm mt-1" type="number" value={filters.min_price}
                  onChange={e => setFilters(f => ({ ...f, min_price: e.target.value }))} data-testid="pfilter-min" />
              </div>
              <div>
                <label className="text-xs text-[var(--zc-text-3)]">Max $/hr</label>
                <input className="zc-input h-10 px-3 w-full text-sm mt-1" type="number" value={filters.max_price}
                  onChange={e => setFilters(f => ({ ...f, max_price: e.target.value }))} data-testid="pfilter-max" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[var(--zc-text-3)]">Sort</label>
              <select className="zc-input h-10 px-3 w-full text-sm mt-1" value={filters.sort}
                onChange={e => setFilters(f => ({ ...f, sort: e.target.value }))} data-testid="pfilter-sort">
                <option value="price_asc">Price · low to high</option>
                <option value="price_desc">Price · high to low</option>
                <option value="rating">Top rated</option>
                <option value="newest">Newest</option>
              </select>
            </div>
            <button onClick={apply} className="w-full h-11 zc-btn-primary text-sm flex items-center justify-center gap-2" data-testid="pfilter-apply">
              <Search size={14} /> Apply filters
            </button>
          </div>
        </aside>

        <div>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="text-sm text-[var(--zc-text-2)]" data-testid="parking-count">
              {loading ? "Searching parking…" : `${spots.length} parking ${spots.length === 1 ? "space" : "spaces"} found`}
            </div>
            <div className="inline-flex items-center bg-[var(--zc-bg-3)] rounded-full p-1" data-testid="parking-view-toggle">
              <button
                onClick={() => setView("list")}
                className={"px-4 h-8 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition " +
                  (view === "list" ? "bg-white shadow-sm text-[var(--zc-text)]" : "text-[var(--zc-text-3)]")}
                data-testid="parking-view-list">
                <List size={12} /> List
              </button>
              <button
                onClick={() => setView("map")}
                className={"px-4 h-8 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition " +
                  (view === "map" ? "bg-white shadow-sm text-[var(--zc-text)]" : "text-[var(--zc-text-3)]")}
                data-testid="parking-view-map">
                <MapIcon size={12} /> Map
              </button>
            </div>
          </div>

          {view === "list" ? (
            <>
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="parking-grid">
                {spots.map((p, i) => <ParkingCard key={p.spot_id} p={p} index={i} />)}
              </div>
              {!loading && spots.length === 0 && (
                <div className="zc-card p-10 text-center text-[var(--zc-text-3)]" data-testid="parking-empty">
                  No spots match your filters. Loosen the search.
                </div>
              )}
            </>
          ) : (
            <ParkingMap spots={spots} />
          )}
        </div>
      </div>
    </div>
  );
}
